import { action, computed, observable, reaction, runInAction } from 'mobx';
import { toast } from 'react-toastify';

import agent from '../api/agent';
import {
  formatTicketDisp,
  getOptions,
  PROCESS_STATE,
} from '../common/util/util';
import { IOption } from '../models/option';
import { IQrTicket, IQrTicketFilters } from '../models/qrticket';
import { RootStore } from './rootStore';

export default class QrTicketStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.qrTicketFilters,
      () => {
        this.qrTicketsRegistry.clear();
        this.loadQrTickets();
      }
    );
  }

  @observable csvData: any[] = [];
  @observable ticketReserveStatusOptions: IOption[] = [];
  @observable ticketEnterStatusOptions: IOption[] = [];
  @observable qrTicketsCount = 0;
  @observable qrTicketsRegistry = new Map();
  @observable
  qrTicketFilters: IQrTicketFilters | null = null;
  @observable loading = false;
  @observable submitting = false;
  @observable searchMode = true;

  @computed get qrTickets() {
    return Array.from(this.qrTicketsRegistry.values());
  }

  @computed get activeQrTickets() {
    return Array.from(this.qrTicketsRegistry.values()).filter(
      (a) => a.deleteFlagEnum === PROCESS_STATE.NotYet
    );
  }

  @computed get ticketReserveStatusFilterOptions() {
    const options = Array.from(this.ticketReserveStatusOptions.values());
    options.unshift({ key: '', value: '', text: '' });
    return options;
  }

  @computed get ticketEnterStatusFilterOptions() {
    const options = Array.from(this.ticketEnterStatusOptions.values());
    options.unshift({ key: '', value: '', text: '' });
    return options;
  }

  @action setSearchMode = (searchMode: boolean) => {
    this.searchMode = searchMode;
  };

  @action setQrTicketFilters = (qrTicketFilters: IQrTicketFilters) => {
    this.qrTicketFilters = qrTicketFilters;
  };

  @action loadQrTicketAttributes = async () => {
    this.loading = true;
    try {
      const attributes = await agent.QrTickets.attributes();
      runInAction('loading qrTicket attributes', () => {
        this.ticketReserveStatusOptions = getOptions(
          attributes.ticketReserveStatuses
        );
        this.ticketEnterStatusOptions = getOptions(
          attributes.ticketEnterStatuses
        );
        this.loading = false;
      });
    } catch (error) {
      runInAction('load qrTicket attributes error', () => {
        this.loading = false;
      });
    }
  };

  @action loadQrTickets = async () => {
    this.loading = true;
    try {
      const qrTickets = await agent.QrTickets.list(this.qrTicketFilters!);
      runInAction('loading qrTickets', () => {
        qrTickets.forEach((qrTicket) => {
          this.qrTicketsRegistry.set(qrTicket.qrticketId, qrTicket);
        });
        this.qrTicketsCount = qrTickets.length;
        this.loading = false;
      });
    } catch (error) {
      runInAction('load qrTickets error', () => {
        this.loading = false;
      });
      error.response &&
        error.response.data &&
        error.response.data.errors &&
        toast.error(Object.values(error.response.data.errors).flat().join());
    }
  };

  @action dlQrTickets = async (salesId: string) => {
    this.submitting = true;
    try {
      const data = await agent.QrTickets.download(salesId);
      runInAction('loading QrTickets csvdata', () => {
        this.csvData = data.map((item: IQrTicket) => ({
          チケット名日本語: item.ticketNameJp,
          チケット名英語: item.ticketNameEn,
          有効期限日: formatTicketDisp(item.limitMonthDate),
          テナント: item.mdTdTicketTypeName,
          QRコード: item.qrcode,
        }));
        this.submitting = false;
      });
    } catch (error) {
      runInAction('load QrTickets csvdata error', () => {
        this.submitting = false;
      });
    }
  };

  @action partiallyDlQrTickets = async (
    salesId: string,
    qrticketIds: number[]
  ) => {
    this.submitting = true;
    try {
      const data = await agent.QrTickets.partiallydownload(
        salesId,
        qrticketIds
      );
      runInAction('loading QrTickets partially csvdata', () => {
        this.csvData = data.map((item: IQrTicket) => ({
          チケット名日本語: item.ticketNameJp,
          チケット名英語: item.ticketNameEn,
          有効期限日: formatTicketDisp(item.limitMonthDate),
          テナント: item.mdTdTicketTypeName,
          QRコード: item.qrcode,
        }));
        this.submitting = false;
      });
    } catch (error) {
      runInAction('load QrTickets partially csvdata error', () => {
        this.submitting = false;
      });
    }
  };

  @action printQrTickets = async (salesId: string) => {
    this.submitting = true;
    try {
      await agent.QrTickets.print(salesId);
      runInAction('printing QrTickets pdfFile', () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction('print QrTickets pdfFile error', () => {
        this.submitting = false;
      });
    }
  };

  @action partiallyPrintQrTickets = async (
    salesId: string,
    qrticketIds: number[]
  ) => {
    this.submitting = true;
    try {
      await agent.QrTickets.partiallyprint(salesId, qrticketIds);
      runInAction('printing QrTickets pdfFile', () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction('print QrTickets pdfFile error', () => {
        this.submitting = false;
      });
    }
  };

  @action issueQrTickets = async (salesId: string, qrticketIds: number[]) => {
    this.submitting = true;
    try {
      await agent.QrTickets.issue(salesId, qrticketIds);
      runInAction('issuing QrTickets', () => {
        qrticketIds.forEach((qrticketId) => {
          const qrTicket = this.qrTicketsRegistry.get(qrticketId);
          qrTicket.ticketReserveStatusEnum = PROCESS_STATE.Done;
          qrTicket.ticketReserveTime = new Date();
          this.qrTicketsRegistry.set(qrTicket.qrticketId, qrTicket);
        });
        this.submitting = false;
      });
    } catch (error) {
      runInAction('issuing QrTickets error', () => {
        this.submitting = false;
      });
      toast.error('発券処理でエラーが発生しました');
      console.log(error);
    }
  };

  @action enter = async (qrticketId: number) => {
    this.submitting = true;
    try {
      await agent.QrTickets.enter(qrticketId);
      runInAction(() => {
        const qrTicket = this.qrTicketsRegistry.get(qrticketId);
        qrTicket.ticketEnterStatusEnum = PROCESS_STATE.Done;
        qrTicket.startTime1 = new Date();
        this.qrTicketsRegistry.set(qrTicket.qrticketId, qrTicket);
        this.submitting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.submitting = false;
      });
      error.response &&
        error.response.data &&
        error.response.data.errors &&
        toast.error(Object.values(error.response.data.errors).flat().join());
    }
  };

  @action exit = async (qrticketId: number) => {
    this.submitting = true;
    try {
      await agent.QrTickets.exit(qrticketId);
      runInAction(() => {
        const qrTicket = this.qrTicketsRegistry.get(qrticketId);
        qrTicket.ticketEnterStatusEnum = PROCESS_STATE.NotYet;
        qrTicket.startTime1 = '';
        this.qrTicketsRegistry.set(qrTicket.qrticketId, qrTicket);
        this.submitting = false;
      });
    } catch (error) {
      toast.error('Problem exiting error');
      runInAction(() => {
        this.submitting = false;
      });
    }
  };

  @action deleteQrTickets = async (qrticketIds: number[]) => {
    this.submitting = true;
    try {
      await agent.QrTickets.delete(qrticketIds);
      runInAction('deleting qrTicket', () => {
        qrticketIds.forEach((qrticketId) => {
          const qrTicket = this.qrTicketsRegistry.get(qrticketId);
          qrTicket.deleteFlagEnum = PROCESS_STATE.Done;
          this.qrTicketsRegistry.set(qrTicket.qrticketId, qrTicket);
        });
        this.submitting = false;
      });
    } catch (error) {
      runInAction('deleting qrTicket error', () => {
        this.submitting = false;
      });
      toast.error('削除処理でエラーが発生しました');
      console.log(error);
    }
  };
}
